import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import ChatDotsIcon from 'public/bee2-ui/img/icon/jsx/ChatDots';
import EmailIcon from 'public/bee2-ui/img/icon/jsx/Email';
import { COUNTRIES, getCountry } from '@/utils/i18n';
import { IS_DEV } from '@/utils/env';
import useStore from '@/store/useStore';
import { subscribeNewsletter, postKlaviyo, klaviyoIdentify } from '@/services/forms.service';
import { sendGaEvent, trackEvent } from '@/utils/gaDataPush';
import { getDataAttributes } from '@/utils/dataAttributes';
import { FooterItemClickProps, FooterItemProp } from '@/store/types/IBuilder';
import { sha256 } from '@/utils/utils';
import { openDEChat } from '@/components/DigitalEngagementChat/DigitalEngagementChat';
import useMD5HashEmail from '@/hooks/useMD5HashEmail';
import { FadeIn } from './components/FadeIn';

const year = new Date().getFullYear();
const setGeCookie = (code, currency, locale) => {
  Cookies.set('GlobalE_Data', `{"countryISO": "${code}", "currencyCode": "${currency}",  "cultureCode": "en-GB"}`, {
    path: '/',
    secure: true,
    domain: `.${window.location.hostname.replace('www.', '')}`,
  });
  localStorage.setItem('glCountry', '');
  Cookies.set('sel_country', code, { path: '/', secure: true });
  window.location.href = `/${locale}${locale ? '/' : ''}`;
};
const getCookieSelCountry = country => {
  if (country === undefined) {
    return true;
  }

  return country === 'US' || country === 'CA';
};
export default function Footer({ links, className }) {
  const countries = [...COUNTRIES];
  const [isNwlVisible, setIsNwlVisible] = useState(false);
  const [isHiddenYourEmailAddress, setIsHiddenYourEmailAddress] = useState(true);
  const [checkPolicy, setcheckPolicy] = useState(false);
  const [trackOrderUrl, setTrackOrderUrl] = useState('/track-order/');
  const [trackOrderAttr, setTrackOrderAttr] = useState(false);
  const { localGeToggle } = useStore(state => state.glLocation);
  const user = useStore(state => state.user);
  const setEmailUsModalVisible = useStore(state => state.setEmailUsModalVisible);
  const { isEuropeanUnionMember } = useStore(state => state.user);
  const isEurope = getCountry().code === 'GB' || isEuropeanUnionMember;
  const { store, telCode } = useStore(state => state.showroom);
  const [phone, setPhone] = useState('800.691.0952');

  const form = useForm();
  const { hashEmail } = useMD5HashEmail();
  const isGlobalE = localGeToggle !== null && !localGeToggle;

  useEffect(() => {
    if (isGlobalE) {
      (window as any).getGlobalEPhoneNumber().then(setPhone);
      (window as any).GlobalECountryCode.then(code => {
        if (!['US', 'CA'].includes(code)) {
          setTrackOrderAttr(true);
          setTrackOrderUrl(
            IS_DEV ? 'https://www.bglobale.com/Order/Track/mZq0' : 'https://web.global-e.com/Order/Track/mZq0'
          );
        }
      });
    } else setPhone(`${store?.tel}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalE, store]);

  useEffect(() => {
    const showTextMeUpdatesOffers =
      (user.ipCountry === 'US' || user.ipCountry === 'CA') && getCookieSelCountry(Cookies.get('sel_country'));

    setIsHiddenYourEmailAddress(showTextMeUpdatesOffers);
  }, [user.ipCountry]);

  const {
    register,
    getValues,
    trigger,
    formState: { errors },
  } = form;

  function onClickEmailUs(event, onClickParams) {
    pageDataPush(event, onClickParams);
    setEmailUsModalVisible(true);
  }

  async function validateEmail() {
    const isValidEmail = await trigger();
    const email = getValues('email');

    if (isValidEmail) {
      postKlaviyo('Footer Signup Form', email);
      klaviyoIdentify({ email });
      subscribeNewsletter(email)
        .then(async () => {
          const hashedEmail = await hashEmail(email);
          const { location } = window as any;
          (window as any).dataLayer.push({
            userEmail: email,
            userHashedEmail: await sha256(email),
            userPhoneNumber: '',
            userFirstName: '',
            userLastName: '',
            hashed_user_email_md5: hashedEmail,
          });
          (window as any).dataLayer.push({
            event: 'submit_email',
            location: 'footer',
          });
          (window as any).dataLayer.push({ event: 'Subscribe via Footer' });
          sendGaEvent({
            action: 'Footer',
            category: 'Newsletter Subscription',
            label: `${location.host}${location.pathname}${location.search}`,
          });
          setIsNwlVisible(true);
        })
        .catch(() => {
          // eslint-disable-next-line no-alert
          alert('Sent failed. Please try again.');
        });
    }
  }

  const keyDownHandler = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      validateEmail();
    }
  };

  const pageDataPush = (event: any, clickParams: FooterItemClickProps) => {
    event.preventDefault();
    (window as any).homePageDataPush(
      clickParams.event,
      clickParams.linkUrl || '',
      clickParams.linkText || '',
      clickParams.location
    );

    trackEvent(event);

    if (clickParams.linkUrl) {
      window.location.href = clickParams.linkUrl;
    }
  };

  const getInteractiveItem = (item: FooterItemProp) => {
    const templates = {
      'live chat': (
        <a
          onClick={event => {
            openDEChat();
            pageDataPush(event, item.onClickParams);
          }}
          href="#"
          {...getDataAttributes(item)}
          className="live_chat_button  live_chat_footer_desktop"
          data-acsb-clickable="true"
          data-acsb-navigable="true"
          data-acsb-now-navigable="true"
          data-acsb-menu="a"
          role="button"
          data-acsb-menu-root-link="true"
          data-acsb-tooltip="Use ←/→ to navigate"
          data-acsb-hover="true"
        >
          Live Chat
        </a>
      ),
      'book appointment': (
        <a
          onClick={trackEvent}
          {...getDataAttributes(item)}
          data-noninteract="true"
          data-acsb-clickable="true"
          data-acsb-navigable="true"
          data-acsb-now-navigable="true"
          data-acsb-menu="a"
          data-acsb-menu-root-link="true"
          data-acsb-hover="true"
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              trackEvent(e);
            }
          }}
        >
          <span
            className="acsb-sr-only"
            data-acsb-sr-only="true"
            data-acsb-force-visible="true"
            aria-hidden="false"
            data-acsb-hidden="false"
          >
            locations
          </span>
          Book Appointment
        </a>
      ),
      'email us': (
        <a
          onClick={event => onClickEmailUs(event, item.onClickParams)}
          href="#"
          id="recaptcha_footer"
          {...getDataAttributes(item)}
          data-acsb-clickable="true"
          data-acsb-navigable="true"
          data-acsb-now-navigable="true"
          data-acsb-menu="a"
          role="button"
          data-acsb-menu-root-link="true"
          data-acsb-hover="true"
        >
          Email Us
        </a>
      ),
      'investor relations': (
        <a
          onClick={event => pageDataPush(event, item.onClickParams)}
          href={item?.url}
          {...getDataAttributes(item)}
          data-acsb-clickable="true"
          data-acsb-navigable="true"
          data-acsb-now-navigable="true"
          data-acsb-menu="a"
          data-acsb-menu-root-link="true"
          data-acsb-hover="true"
        >
          Investor Relations
        </a>
      ),
      accessibility: (
        <a
          onClick={event => pageDataPush(event, item.onClickParams)}
          href="#"
          {...getDataAttributes(item)}
          data-acsb="trigger"
          style={{ fontSize: '13px !important' }}
          tabIndex={0}
          role="button"
          data-acsb-menu="a"
          data-acsb-menu-root-link="true"
          data-acsb-hover="true"
        >
          Accessibility
        </a>
      ),
      'accessibility info': (
        <a
          onClick={event => pageDataPush(event, item.onClickParams)}
          href="/accessibility/"
          {...getDataAttributes(item)}
          style={{ fontSize: '13px !important' }}
          data-noninteract="true"
          data-acsb-clickable="true"
          data-acsb-navigable="true"
          data-acsb-now-navigable="true"
          data-acsb-menu="a"
          data-acsb-menu-root-link="true"
          data-acsb-hover="true"
        >
          Accessibility Info
        </a>
      ),
      'track your order': (
        <>
          <a
            href="/track-order/"
            {...getDataAttributes(item)}
            onClick={trackEvent}
            data-noninteract="true"
            data-acsb-clickable="true"
            data-acsb-navigable="true"
            data-acsb-now-navigable="true"
            data-acsb-menu="a"
            data-acsb-menu-root-link="true"
            data-acsb-tooltip="Use ←/→ to navigate"
            data-acsb-hover="true"
            data-ge-hide-countries="all"
            // eslint-disable-next-line react/no-unknown-property
            trackOrderGE={item?.url === '/track-order/' && trackOrderAttr ? '' : null}
          >
            Track Your Order
          </a>
          <a data-ge-show-countries="all" href={trackOrderUrl}>
            Track Your Order
          </a>
        </>
      ),
    };

    return templates[item.label.toLowerCase()];
  };

  const openSocialMedia = (event: any, dataEvent: { source: string; url: string; label: string }) => {
    event.preventDefault();

    const { source, url, label } = dataEvent;
    (window as any).socialClickDataPush(source, url, label);
    (window as any).open(url, '_blank', 'noopener,noreferrer');
  };

  const showTextMeUpdatesOffers =
    (user.ipCountry === 'US' || user.ipCountry === 'CA') && getCookieSelCountry(Cookies.get('sel_country'));

  let redirectAttentiveLandingPage = '';
  if (user.ipCountry === 'US') {
    redirectAttentiveLandingPage = 'https://brilliantearth.attn.tv/p/OiJ/1000giftcard';
  } else if (user.ipCountry === 'CA') {
    redirectAttentiveLandingPage = 'https://brilliantearth-ca.attn.tv/p/YnY/landing-page';
  }

  return (
    <div className={className}>
      <div className="ir217-footer">
        <div className="wrapper ir217-footer-links container1170">
          <div className="tw-grid tw-grid-cols-3 tw-gap-x-12">
            {links &&
              links.map(({ footerItem }, footerLinkIndex: number) => (
                <div key={footerLinkIndex}>
                  <div className="column" role="navigation" aria-label={footerItem.title}>
                    <div className="h3">
                      <div
                        data-parent="#accordion"
                        aria-controls="collapseOne"
                        className="swith collapsed"
                        data-toggle="collapse"
                        aria-hidden="true"
                        data-acsb-hidden="true"
                      >
                        <span
                          className="symbol"
                          aria-hidden="true"
                          data-acsb-hidden="true"
                          data-acsb-force-hidden="true"
                        />
                      </div>
                      <a
                        onClick={trackEvent}
                        href={footerItem?.url}
                        className="link tw-font-[500]"
                        {...getDataAttributes(footerItem)}
                        data-acsb-clickable="true"
                        data-acsb-navigable="true"
                        data-acsb-now-navigable="true"
                        data-acsb-hover="true"
                      >
                        {footerItem?.title}
                      </a>
                    </div>
                    <ul
                      id={`#collapse${footerLinkIndex + 1}`}
                      className="submenu panel-collapse collapse"
                      style={{ height: 'auto' }}
                      data-acsb-menu="ul"
                    >
                      {footerItem.items &&
                        footerItem.items.map((item: FooterItemProp, footerItemIndex: number) => (
                          <li key={footerItemIndex} data-acsb-menu="li" data-acsb-menu-root="true">
                            {item.onClickParams?.event || (item?.url === '/track-order/' && trackOrderAttr) ? (
                              getInteractiveItem(item)
                            ) : (
                              <a
                                onClick={trackEvent}
                                href={item?.url || '#'}
                                {...getDataAttributes(item)}
                                data-noninteract="true"
                                data-acsb-clickable="true"
                                data-acsb-navigable="true"
                                data-acsb-now-navigable="true"
                                data-acsb-menu="a"
                                data-acsb-menu-root-link="true"
                                data-acsb-tooltip="Use ←/→ to navigate"
                                data-acsb-hover="true"
                                // eslint-disable-next-line react/no-unknown-property
                                trackOrderGE={null}
                              >
                                {item?.url === '/contact/' ? phone : item?.label}
                              </a>
                            )}
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              ))}
            <div className="column">
              <div className="h3">
                <span className="link tw-font-[500]">Sign Up</span>
              </div>
              <ul className="submenu submenu-adress-form !tw-mb-[10px]">
                {showTextMeUpdatesOffers && (
                  <li>
                    <a
                      className="tw-flex tw-flex-wrap tw-content-center tw-gap-[5px]"
                      href={redirectAttentiveLandingPage}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ChatDotsIcon />
                      <p className="tw-m-0 tw-leading-[1.5]">Text me updates and offers</p>
                    </a>
                  </li>
                )}

                <li>
                  <a
                    className="tw-flex tw-flex-wrap tw-content-center tw-gap-[5px] iconsHover"
                    href=""
                    onClick={event => {
                      event.preventDefault();
                      setIsHiddenYourEmailAddress(isHidden => !isHidden);
                    }}
                  >
                    <EmailIcon className="" />
                    <p className="tw-m-0 tw-leading-[1.5]">Email me updates and offers</p>
                  </a>
                </li>
                {!isHiddenYourEmailAddress && (
                  <li className="address-group-wrap be-join-movement">
                    <FadeIn props={{ opacity: isNwlVisible ? 1 : 0 }} visible={isNwlVisible}>
                      <div>Thank you for subscribing to the Brilliant Earth newsletter.</div>
                    </FadeIn>
                    {!isNwlVisible && (
                      <form className="footer-email tw-font-book">
                        <div
                          className={classNames('tw-group tw-flex tw-h-11 tw-w-full tw-relative tw-z-0', {
                            'is-error': errors.email?.message,
                          })}
                        >
                          <input
                            {...register('email', {
                              required: 'Email Address is required.',
                              validate: {
                                isValid: v =>
                                  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/.test(v) ||
                                  'Invalid Email Address.',
                              },
                            })}
                            type="text"
                            onKeyDown={keyDownHandler}
                            className="border-normal tw-peer tw-block tw-w-full tw-appearance-none tw-rounded-none tw-border tw-border-gray tw-bg-transparent tw-px-2 tw-pb-0 tw-pt-4 tw-text-sm/5 tw-outline-none tw-outline-0 focus:tw-border-darkGrey focus:tw-ring-[0.5px] focus:tw-ring-inset focus:tw-ring-darkGrey group-[.is-error]:tw-border-error group-[.is-error]:tw-ring-error tw-font-AvenirBook"
                            placeholder=" "
                            id="footer_newsletter"
                            maxLength={75}
                            aria-label="Your Email Address"
                          />
                          <label
                            htmlFor="footer_newsletter"
                            className="tw-pointer-events-none tw-absolute tw-left-2 tw-top-3.5 tw-origin-[0] -tw-translate-y-2 tw-scale-75 tw-transform tw-text-sm tw-font-normal tw-text-grey500 tw-duration-300 group-[.is-error]:!tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-placeholder-shown:tw-text-darkGrey peer-focus:-tw-translate-y-2 peer-focus:tw-scale-75 peer-focus:tw-text-grey500 tw-font-AvenirBook"
                          >
                            Your Email Address
                          </label>
                          <button
                            className="border-normal tw-size-11 tw-min-w-11 tw-appearance-none tw-rounded-none tw-bg-darkGreen tw-pt-1.5 tw-uppercase tw-text-white tw-outline-none hover:tw-bg-darkGreenHover"
                            type="button"
                            aria-label="Submit Email Address"
                            onClick={validateEmail}
                          >
                            <svg
                              fill="none"
                              height="17"
                              viewBox="0 0 10 17"
                              width="10"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1.42462 1L8.84924 8.42462L1.42462 15.8492"
                                stroke="white"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                        {errors.email?.message && (
                          <div className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-error">
                            {errors?.email.message as string}
                          </div>
                        )}
                        {isEurope && (
                          <div className="tw-mb-2 tw-flex tw-items-start tw-mt-[20px]">
                            <input
                              {...register('policy', {
                                required: 'Agreement to our Privacy Policy and Terms and Conditions is required.',
                              })}
                              className={classNames(
                                'border-normal !tw-mt-0 tw-size-[18px] tw-rounded-none tw-border tw-border-[#d9d9d9] tw-text-darkGreen tw-ring-0 checked:tw-border-current focus:!tw-outline-0 focus:tw-ring-0',
                                { 'tw-border-error': errors.policy?.message }
                              )}
                              type="checkbox"
                              data-tw="checkbox"
                              id="footer_agree_policy_checkbox"
                              checked={checkPolicy}
                              onClick={() => setcheckPolicy(!checkPolicy)}
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  setcheckPolicy(!checkPolicy);
                                }
                              }}
                            />
                            <div className="tw-ms-2">
                              <label
                                className="tw-relative tw-top-0.5 tw-mb-0 tw-text-xs tw-font-normal"
                                htmlFor="footer_agree_policy_checkbox"
                              >
                                By checking this box and submitting my email address, I agree to Brilliant Earth&apos;s{' '}
                                <a
                                  href="/en-gb/security-and-privacy-policies/#privacy"
                                  style={{ padding: 0, display: 'inline' }}
                                  className="td-u2"
                                >
                                  Privacy Policy
                                </a>{' '}
                                and{' '}
                                <a
                                  href="/en-gb/terms-and-conditions/"
                                  className="td-u2"
                                  style={{ padding: 0, display: 'inline' }}
                                >
                                  Terms and Conditions.
                                </a>
                              </label>
                              {errors.policy?.message && (
                                <div className="tw-mt-2 tw-mb-2 tw-text-[11px] tw-text-error">
                                  {errors?.policy.message as string}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="join-email" />
                      </form>
                    )}
                  </li>
                )}
              </ul>
              <nav aria-label="Footer Menu">
                <ul className="list-inline ir217-follow-us" data-acsb-menu="ul">
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'youtube',
                          url: 'https://www.youtube.com/@BrilliantEarthInc',
                          label: 'YouTube',
                        });

                        trackEvent(e, true);
                      }}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href="https://www.youtube.com/@BrilliantEarthInc"
                      className="iconfont iconfont-youtube hover:tw-no-underline"
                      aria-label="YouTube"
                      data-event1-category="Desktop Footer"
                      data-event1-action="Click"
                      data-event1-label="YouTube"
                      data-acsb-tooltip="YouTube | New Window"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-menu="a"
                      data-acsb-menu-root-link="true"
                    >
                      <span
                        className="sr-only"
                        aria-hidden="false"
                        data-acsb-hidden="false"
                        data-acsb-sr-only="true"
                        data-acsb-force-visible="true"
                      >
                        YouTube YouTube New Window
                      </span>
                    </a>
                  </li>
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'instagram',
                          url: 'https://www.instagram.com/brilliantearth/',
                          label: 'Instagram',
                        });
                        trackEvent(e, true);
                      }}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href="https://www.instagram.com/brilliantearth/"
                      className="iconfont iconfont-instagram hover:tw-no-underline"
                      aria-label="Instagram"
                      data-event1-category="Desktop Footer"
                      data-event1-action="Click"
                      data-event1-label="Instagram"
                      data-acsb-tooltip="Instagram | New Window"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-menu="a"
                      data-acsb-menu-root-link="true"
                    >
                      <span
                        className="sr-only"
                        aria-hidden="false"
                        data-acsb-hidden="false"
                        data-acsb-sr-only="true"
                        data-acsb-force-visible="true"
                      >
                        Instagram Instagram New Window
                      </span>
                    </a>
                  </li>
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'facebook',
                          url: 'https://www.facebook.com/brilliantearth',
                          label: 'Facebook',
                        });
                        trackEvent(e, true);
                      }}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href="https://www.facebook.com/brilliantearth"
                      className="iconfont iconfont-facebook hover:tw-no-underline active:tw-outline-none focus:tw-outline-none"
                      aria-label="Facebook"
                      data-event1-category="Desktop Footer"
                      data-event1-action="Click"
                      data-event1-label="Facebook"
                      data-acsb-tooltip="Facebook | New Window"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="false"
                      data-acsb-menu="a"
                      role="button"
                      data-acsb-menu-root-link="true"
                      data-acsb-force-unnavigable="true"
                      tabIndex={-1}
                      aria-hidden="true"
                      data-acsb-hidden="true"
                      data-acsb-force-hidden="true"
                    >
                      <span
                        className="sr-only"
                        aria-hidden="false"
                        data-acsb-hidden="false"
                        data-acsb-sr-only="true"
                        data-acsb-force-visible="true"
                      >
                        Facebook
                      </span>
                    </a>
                  </li>
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'pinterest',
                          url: 'https://www.pinterest.com/brilliantearth/',
                          label: 'Pinterest',
                        });
                        trackEvent(e, true);
                      }}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href="https://www.pinterest.com/brilliantearth/"
                      className="iconfont iconfont-pinterest hover:tw-no-underline"
                      data-event1-category="Desktop Footer"
                      data-event1-action="Click"
                      data-event1-label="Pinterest"
                      data-acsb-tooltip="Pinterest | New Window"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-menu="a"
                      data-acsb-menu-root-link="true"
                    >
                      <span
                        className="sr-only"
                        aria-hidden="false"
                        data-acsb-hidden="false"
                        data-acsb-sr-only="true"
                        data-acsb-force-visible="true"
                      >
                        Pinterest Pinterest New Window
                      </span>
                    </a>
                  </li>
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'twitter',
                          url: 'https://twitter.com/BrilliantEarth',
                          label: 'Twitter',
                        });
                        trackEvent(e, true);
                      }}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href="https://twitter.com/BrilliantEarth"
                      className="iconfont iconfont-twitter hover:tw-no-underline"
                      data-event1-category="Desktop Footer"
                      data-event1-action="Click"
                      data-event1-label="Twitter"
                      data-acsb-tooltip="Twitter | New Window"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-menu="a"
                      data-acsb-menu-root-link="true"
                    >
                      <span className="sr-only">Twitter</span>
                    </a>
                  </li>
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'linkedin',
                          url: 'https://www.linkedin.com/company/brilliantearth',
                          label: 'Linkedin',
                        });
                        trackEvent(e, true);
                      }}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href="https://www.linkedin.com/company/brilliantearth"
                      className="iconfont iconfont-linkedin hover:tw-no-underline"
                      data-event1-category="Desktop Footer"
                      data-event1-action="Click"
                      data-event1-label="Linkedin"
                      data-acsb-tooltip="Linkedin | New Window"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-menu="a"
                      data-acsb-menu-root-link="true"
                    >
                      <span
                        className="sr-only"
                        aria-hidden="false"
                        data-acsb-hidden="false"
                        data-acsb-sr-only="true"
                        data-acsb-force-visible="true"
                      >
                        Linkedin LinkedIn New Window
                      </span>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
        <div className="ir249-footer-serves">
          <div className="wrapper container1170">
            <ul className="list-inline tw-leading-normal" id="ir217-copyright">
              <li className="first -inline">
                <span>©{year} Brilliant Earth, LLC</span>
              </li>
              <li>
                <span>
                  <a
                    onClick={trackEvent}
                    href="/terms-and-conditions/"
                    data-event1-category="Desktop Footer"
                    data-event1-action="Click"
                    data-event1-label="Terms & Conditions"
                  >
                    Terms &amp; Conditions
                  </a>
                </span>
              </li>
              <li>
                <span>
                  <a
                    onClick={trackEvent}
                    href="/security-and-privacy-policies/#privacy"
                    data-event1-category="Desktop Footer"
                    data-event1-action="Click"
                    data-event1-label="Privacy Policy"
                  >
                    Privacy Policy
                  </a>
                </span>
              </li>
              <li>
                <span>
                  <a
                    onClick={trackEvent}
                    href="/security-and-privacy-policies/#interest_based_ads"
                    data-event1-category="Desktop Footer"
                    data-event1-action="Click"
                    data-event1-label="Interest Based Ads"
                  >
                    Interest-Based Ads
                  </a>
                </span>
              </li>
              <li>
                <span>
                  <a
                    onClick={e => {
                      trackEvent(e);
                      window['OneTrust']?.ToggleInfoDisplay();
                    }}
                    href="#"
                    className="focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]"
                    role="button"
                    tabIndex={0}
                    data-event1-category="Desktop Footer"
                    data-event1-action="Click"
                    data-event1-label="Do Not Share My Personal Information"
                  >
                    Do Not Sell or Share My Personal Information
                  </a>
                </span>
              </li>
              <li>
                <span>
                  <a
                    onClick={trackEvent}
                    href="/ca-transparency-act/"
                    data-event1-category="Desktop Footer"
                    data-event1-action="Click"
                    data-event1-label="CA Transparency Act"
                  >
                    CA Transparency Act
                  </a>
                </span>
              </li>
              <li>
                <span>
                  <a
                    onClick={trackEvent}
                    href="/security-and-privacy-policies/#CPRA"
                    data-event1-category="Desktop Footer"
                    data-event1-action="Click"
                    data-event1-label="CPRA"
                  >
                    CPRA
                  </a>
                </span>
              </li>
              <li>
                <span>
                  <a
                    onClick={trackEvent}
                    href="/sitemap/"
                    data-event1-category="Desktop Footer"
                    data-event1-action="Click"
                    data-event1-label="Site Map"
                  >
                    Site Map
                  </a>
                </span>
              </li>
              <li className="first visible-xs-inline">
                <span>©{year} Brilliant Earth, LLC</span>
              </li>
            </ul>
          </div>{' '}
          <div className="wrapper container1170">
            <ul className="list-inline tw-text-center tw-text-[10px]">
              {countries
                .sort((a, b) => a.code.localeCompare(b.code))
                .map(({ code, currency, locale }, i) => (
                  <li key={code + i}>
                    <a
                      data-category="Desktop Footer"
                      data-action="Click"
                      className="hover:!tw-no-underline focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]"
                      data-label={`Subfolder | ${currency}`}
                      onClick={() => setGeCookie(code, currency, locale)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          setGeCookie(code, currency, locale);
                        }
                      }}
                    >
                      {i > 0 && '|'}&nbsp;&nbsp; {currency}
                    </a>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
